import { useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';

import { Lease, Unit } from '@urbanmix-tech/shared-js';
import { getLeases, deleteLease } from '@/data';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { getLeaseCategory, getCategoryColor, type LeaseCategory } from '@/helpers/leaseHelpers';
import { useLoading } from '@/shared/context/UIContextProvider';
import { LeaseCard } from '../BuildingTabs/LeaseCard';

export type LeasesTabContentProps = {
  unit: Unit;
};

export function LeasesTabContent({ unit }: LeasesTabContentProps) {
  const { createLoading } = useLoading();
  const [leases, setLeases] = useState<Lease[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLeases = async () => {
    if (!unit.projectId || !unit.id) return;

    const loader = createLoading();
    loader.startLoading({ title: 'Loading leases...' });

    try {
      const unitLeases = await getLeases({projectId:unit.projectId, unitId: unit.id });
      if (!Array.isArray(unitLeases)) {
        throw new Error('Invalid leases data');
      }

      setLeases(unitLeases);
    } catch (error) {
      console.error('Error fetching leases:', error);
    } finally {
      loader.stopLoading();
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeases();
  }, [unit.projectId, unit.id, createLoading]);

  const handleDeleteLease = async (leaseId: string) => {
    const loader = createLoading();
    loader.startLoading({ title: 'Deleting lease...' });

    try {
      await deleteLease(leaseId);
      // Refresh leases data after deletion
      await fetchLeases();
    } catch (error) {
      console.error('Error deleting lease:', error);
    } finally {
      loader.stopLoading();
    }
  };

  const categorizedLeases = useMemo(() => {
    const result: Record<LeaseCategory, Lease[]> = {
      active: [],
      future: [],
      past: [],
      draft: [],
    };

    leases.forEach((lease) => {
      const category = getLeaseCategory(lease);
      result[category].push(lease);
    });

    // Sort each category by start date (most recent first for past, earliest first for future)
    result.active.sort((a, b) => {
      const aDate = convertPseudoDateToRealDate(a.leaseStartDate as any);
      const bDate = convertPseudoDateToRealDate(b.leaseStartDate as any);
      return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
    });

    result.future.sort((a, b) => {
      const aDate = convertPseudoDateToRealDate(a.leaseStartDate as any);
      const bDate = convertPseudoDateToRealDate(b.leaseStartDate as any);
      return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
    });

    result.past.sort((a, b) => {
      const aDate = convertPseudoDateToRealDate(a.leaseEndDate as any);
      const bDate = convertPseudoDateToRealDate(b.leaseEndDate as any);
      return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
    });

    result.draft.sort((a, b) => {
      const aDate = convertPseudoDateToRealDate(a.leaseStartDate as any);
      const bDate = convertPseudoDateToRealDate(b.leaseStartDate as any);
      return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
    });

    return result;
  }, [leases]);

  const totalLeases = leases.length;

  if (loading) {
    return (
      <div className="h-full w-[640px] flex items-center justify-center">
        <Typography variant="body1" color="textSecondary">
          Loading leases...
        </Typography>
      </div>
    );
  }

  return (
    <div className="h-full w-[640px] overflow-y-auto">
      <Stack className="bg-white/5 p-16" gap="16px">
        <Typography variant="caption" className="tracking-tight text-stone-200">
          {totalLeases} lease{totalLeases !== 1 ? 's' : ''}
        </Typography>

        {totalLeases === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Typography variant="body1" color="textSecondary">
              No leases found for this unit
            </Typography>
          </div>
        ) : (
          <Stack gap="24px">
            {/* Active Leases */}
            {categorizedLeases.active.length > 0 && (
              <Stack gap="12px">
                <Typography variant="subtitle2" className="text-green-400 font-semibold">
                  Active ({categorizedLeases.active.length})
                </Typography>
                {categorizedLeases.active.map((lease) => (
                  <LeaseCard
                    key={lease.id}
                    lease={lease}
                    unit={unit}
                    processes={[]}
                    showEdit={true}
                    showCategory={true}
                    borderColorOverride={getCategoryColor('active')}
                    onLeaseUpdated={() => {
                      // Refresh leases data
                      fetchLeases();
                    }}
                    onDelete={handleDeleteLease}
                  />
                ))}
              </Stack>
            )}

            {/* Future Leases */}
            {categorizedLeases.future.length > 0 && (
              <Stack gap="12px">
                <Typography variant="subtitle2" className="text-blue-400 font-semibold">
                  Future ({categorizedLeases.future.length})
                </Typography>
                {categorizedLeases.future.map((lease) => (
                  <LeaseCard
                    key={lease.id}
                    lease={lease}
                    unit={unit}
                    processes={[]}
                    showEdit={true}
                    showCategory={true}
                    borderColorOverride={getCategoryColor('future')}
                    onLeaseUpdated={() => {
                      // Refresh leases data
                      fetchLeases();
                    }}
                    onDelete={handleDeleteLease}
                  />
                ))}
              </Stack>
            )}

            {/* Draft Leases */}
            {categorizedLeases.draft.length > 0 && (
              <Stack gap="12px">
                <Typography variant="subtitle2" className="text-orange-400 font-semibold">
                  Draft ({categorizedLeases.draft.length})
                </Typography>
                {categorizedLeases.draft.map((lease) => (
                  <LeaseCard
                    key={lease.id}
                    lease={lease}
                    unit={unit}
                    processes={[]}
                    showEdit={true}
                    showCategory={true}
                    borderColorOverride={getCategoryColor('draft')}
                    onLeaseUpdated={() => {
                      // Refresh leases data
                      fetchLeases();
                    }}
                    onDelete={handleDeleteLease}
                  />
                ))}
              </Stack>
            )}

            {/* Past Leases */}
            {categorizedLeases.past.length > 0 && (
              <Stack gap="12px">
                <Typography variant="subtitle2" className="text-zinc-400 font-semibold">
                  Past ({categorizedLeases.past.length})
                </Typography>
                {categorizedLeases.past.map((lease) => (
                  <LeaseCard
                    key={lease.id}
                    lease={lease}
                    unit={unit}
                    processes={[]}
                    showEdit={true}
                    showCategory={true}
                    borderColorOverride={getCategoryColor('past')}
                    onLeaseUpdated={() => {
                      // Refresh leases data
                      fetchLeases();
                    }}
                    onDelete={handleDeleteLease}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </div>
  );
}
